import { useState } from "react";
import {
  Badge, Dialog, DialogContent, DialogHeader, DialogTitle, EmptyState, Group, RegionCargando, Skeleton, Stack, Text,
} from "@/components/ui";
import { formatFechaHora } from "@/shared/format/fecha";
import { useCatalogoNotificaciones, useVersion, useVersiones } from "../../hooks/use-notificaciones";
import { ETIQUETA_CANAL } from "../../utils/lienzo";
import { resumenDestinatarios } from "../../utils/etiquetas";
import { IconHistory } from "@tabler/icons-react";

/** Historial de versiones publicadas · al elegir una se muestra su contenido (solo lectura). */
export function VersionesDialog({ flujoId, abierto, onCerrar }: { flujoId: string; abierto: boolean; onCerrar: () => void }) {
  const { data: versiones, isLoading } = useVersiones(flujoId, abierto);
  const [numero, setNumero] = useState<number | null>(null);
  const { data: version, isLoading: cargandoVersion } = useVersion(flujoId, numero);
  const { data: catalogo } = useCatalogoNotificaciones();
  const nombreEvento = new Map(catalogo.eventos.map((e) => [e.codigo, e.nombre]));

  return (
    <Dialog open={abierto} onOpenChange={(o) => { if (!o) { setNumero(null); onCerrar(); } }}>
      <DialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Versiones publicadas</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <VersionesSkeleton />
        ) : !versiones || versiones.length === 0 ? (
          <EmptyState icon={IconHistory} title="Aún no se ha publicado" description="Publica el borrador para crear la versión 1." compact />
        ) : (
          <div className="grid grid-cols-[220px_1fr] gap-4">
            <ul className="space-y-1">
              {versiones.map((v) => (
                <li key={v.id}>
                  <button
                    type="button"
                    onClick={() => setNumero(v.numero)}
                    className={`w-full rounded-md border px-3 py-2 text-left text-sm transition-colors ${numero === v.numero ? "border-multiva-600" : "border-border hover:bg-muted"}`}
                  >
                    <Group justify="space-between">
                      <span className="font-medium">v{v.numero}</span>
                      <Badge size="xs" color={v.estado === "Publicada" ? "exito" : "slate"}>{v.estado === "Publicada" ? "Vigente" : "Retirada"}</Badge>
                    </Group>
                    <Text size="xs" c="dimmed">{formatFechaHora(v.publicadaAt)}</Text>
                  </button>
                </li>
              ))}
            </ul>
            <div>
              {numero === null ? (
                <Text size="sm" c="dimmed">Elige una versión para ver qué contenía.</Text>
              ) : cargandoVersion || !version ? (
                <ContenidoVersionSkeleton />
              ) : (
                <Stack gap="sm">
                  <Text size="sm" fw={600}>Eventos</Text>
                  <ul className="list-disc pl-5 text-sm">
                    {version.eventos.map((e) => <li key={e.clave}>{nombreEvento.get(e.tipoEvento) ?? e.tipoEvento}</li>)}
                  </ul>
                  <Text size="sm" fw={600}>Notificaciones</Text>
                  <ul className="space-y-1 text-sm">
                    {version.notificaciones.map((n) => {
                      const eventos = version.conexiones
                        .filter((c) => c.notificacionClave === n.clave)
                        .map((c) => version.eventos.find((e) => e.clave === c.eventoClave)?.tipoEvento)
                        .map((t) => (t ? nombreEvento.get(t) ?? t : "?"));
                      return (
                        <li key={n.clave} className="rounded-md border border-border p-2">
                          <Group gap="xs">
                            <span className="font-medium">{n.nombre}</span>
                            <Badge size="xs" color="slate">{ETIQUETA_CANAL[n.canal]}</Badge>
                            {!n.activa ? <Badge size="xs" color="advertencia">Inactiva</Badge> : null}
                          </Group>
                          <Text size="xs" c="dimmed">
                            {n.canal === "Webhook" ? n.url : resumenDestinatarios(n.destinatarios)} · al ocurrir: {eventos.join(", ") || "—"}
                          </Text>
                        </li>
                      );
                    })}
                  </ul>
                </Stack>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Primera carga · lista de versiones a la izquierda y panel de contenido a la derecha. */
function VersionesSkeleton() {
  return (
    <RegionCargando etiqueta="Cargando versiones…" className="grid grid-cols-[220px_1fr] gap-4">
      <div className="space-y-1" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex flex-col gap-1.5 rounded-md border border-border px-3 py-2">
            <div className="flex items-center justify-between">
              <Skeleton height={13} width={28} />
              <Skeleton height={16} width={52} className="rounded-full" />
            </div>
            <Skeleton height={10} width={110} />
          </div>
        ))}
      </div>
      <Skeleton height={12} width={260} className="max-w-full" aria-hidden="true" />
    </RegionCargando>
  );
}

/** Contenido de una versión en carga · eventos (viñetas) y notificaciones (tarjetas). */
function ContenidoVersionSkeleton() {
  return (
    <RegionCargando etiqueta="Cargando versión…" className="flex flex-col gap-2.5">
      <Skeleton height={13} width={60} aria-hidden="true" />
      {["45%", "35%"].map((w, i) => (
        <Skeleton key={i} height={11} width={w} className="ml-5" aria-hidden="true" />
      ))}
      <Skeleton height={13} width={100} className="mt-1" aria-hidden="true" />
      {[0, 1].map((i) => (
        <div key={i} className="flex flex-col gap-1.5 rounded-md border border-border p-2" aria-hidden="true">
          <div className="flex items-center gap-2">
            <Skeleton height={12} width={i === 0 ? 160 : 130} />
            <Skeleton height={16} width={52} className="rounded-full" />
          </div>
          <Skeleton height={10} width="70%" />
        </div>
      ))}
    </RegionCargando>
  );
}
